import type { IVrboConnectionData } from 'uplisting-frontend/models/channel-connection';

export const VRBO_MARKETS = [
  'us',
  'uk',
  'de',
  'fr',
  'es',
  'it',
  'pt',
  'au',
  'nz',
  'ca',
] as const;

export type VrboMarket = (typeof VRBO_MARKETS)[number];

export const VRBO_CONNECTION_STATUSES = [
  'pending',
  'onboarding',
  'mapping',
  'syncing',
  'active',
  'failed',
  'inactive',
] as const;

export type VrboConnectionStatus = (typeof VRBO_CONNECTION_STATUSES)[number];

export type VrboCardAction = 'brandLink' | 'disconnect' | 'edit' | 'retry';

const VRBO_CARD_ACTIONS: Record<VrboConnectionStatus, VrboCardAction[]> = {
  pending: ['brandLink', 'disconnect', 'edit'],
  onboarding: ['brandLink', 'disconnect', 'edit'],
  mapping: ['disconnect'],
  syncing: [],
  active: ['disconnect'],
  failed: ['disconnect', 'retry'],
  inactive: [],
};

const VRBO_STATUS_BADGE_CLASSES: Record<VrboConnectionStatus, string> = {
  pending: 'badge-pending',
  onboarding: 'badge-pending',
  mapping: 'badge-pending',
  syncing: 'badge-pending',
  active: 'badge-success',
  failed: 'badge-failed',
  inactive: 'badge-secondary',
};

export type VrboListingStatus =
  | 'live'
  | 'draft'
  | 'pushing'
  | 'pending_live'
  | 'inactive'
  | 'failed'
  | 'linked'
  | 'not_linked';

const VRBO_LINK_STATUSES: Record<string, VrboListingStatus> = {
  live: 'live',
  active: 'live',
  draft: 'draft',
  pushing: 'pushing',
  pending_live: 'pending_live',
  inactive: 'inactive',
  failed: 'failed',
};

const VRBO_LISTING_STATUS_BADGE_CLASSES: Record<VrboListingStatus, string> = {
  live: 'badge-success',
  draft: 'badge-pending',
  pushing: 'badge-pending',
  pending_live: 'badge-pending',
  inactive: 'badge-failed',
  failed: 'badge-failed',
  linked: 'badge-secondary',
  not_linked: 'badge-secondary',
};

export const VRBO_MAPPING_PROGRESS_STATUSES = [
  'COMPLETE',
  'ERROR',
  'IN_PROGRESS_WITH_PARTNER',
  'VALIDATION_IN_PROGRESS',
  'IN_PROGRESS_WITH_EXPEDIA_GROUP',
] as const;

export type VrboMappingProgressStatus =
  (typeof VRBO_MAPPING_PROGRESS_STATUSES)[number];

export const VRBO_MAPPING_PROCESSING_STATUSES = [
  'MAPPED',
  'MAPPING_ERROR',
  'UNPROCESSED',
  'VALIDATED',
  'VALIDATION_ERROR',
] as const;

export type VrboMappingProcessingStatus =
  (typeof VRBO_MAPPING_PROCESSING_STATUSES)[number];

export type VrboMappingStage =
  | 'complete'
  | 'error'
  | 'with_uplisting'
  | 'validating'
  | 'with_vrbo'
  | 'in_progress';

const VRBO_MAPPING_STAGES: Record<VrboMappingProgressStatus, VrboMappingStage> =
  {
    COMPLETE: 'complete',
    ERROR: 'error',
    IN_PROGRESS_WITH_PARTNER: 'with_uplisting',
    VALIDATION_IN_PROGRESS: 'validating',
    IN_PROGRESS_WITH_EXPEDIA_GROUP: 'with_vrbo',
  };

const VRBO_MAPPING_STAGE_BADGE_CLASSES: Record<VrboMappingStage, string> = {
  complete: 'badge-success',
  error: 'badge-failed',
  with_uplisting: 'badge-pending',
  validating: 'badge-pending',
  with_vrbo: 'badge-pending',
  in_progress: 'badge-pending',
};

export interface IVrboMappingProgressError {
  code: string;
  message: string;
}

export interface IVrboMappingProgressProperty {
  eg_property_id: string;
  supplier_property_id: string | null;
  status: VrboMappingProcessingStatus | null;
  error: IVrboMappingProgressError | null;
}

export interface IVrboMappingProgressReservation {
  eg_property_id: string;
  status: VrboMappingProcessingStatus | null;
  error: IVrboMappingProgressError | null;
}

export interface IVrboMappingProgress {
  status: string | null;
  reservation_status: string | null;
  checked_at: string | null;
  missing_property_ids: string[];
  property_ids_to_archive: string[];
  properties: IVrboMappingProgressProperty[];
  missing_reservations: { eg_property_id: string; eg_reservation_id: string }[];
  reservations: IVrboMappingProgressReservation[];
}

export interface IVrboMappingPropertyError {
  externalId: string;
  code: string;
  message: string;
}

export interface IVrboMappingProgressSummary {
  stage: VrboMappingStage;
  stageBadgeClass: string;
  reservationStage: VrboMappingStage;
  reservationStageBadgeClass: string;
  propertiesTotal: number;
  propertiesComplete: number;
  reservationsTotal: number;
  reservationsComplete: number;
  missingPropertyIds: string[];
  missingReservationsCount: number;
  propertyErrors: IVrboMappingPropertyError[];
  reservationErrors: IVrboMappingPropertyError[];
  checkedAt: Date | null;
}

export type VrboMappingChoice =
  | { kind: 'map'; propertyId: string }
  | { kind: 'import' };

export type VrboMappingChoices = Record<string, VrboMappingChoice>;

export type VrboMappingOutcome = 'map' | 'import' | 'archive';

export interface IVrboMappingLink {
  externalId: string;
  propertyId: string | null;
}

export interface IVrboMappingPayload {
  links: IVrboMappingLink[];
  archiveExternalIds: string[];
}

const LISTING_ERROR = /^Vrbo property (\S+) /;
const PROPERTY_ERROR = /^Property (\S+) /;

export interface IVrboMappingErrors {
  general: string[];
  byListing: Record<string, string[]>;
  byProperty: Record<string, string[]>;
}

export function compareByOriginalId(
  a: { originalId: string },
  b: { originalId: string },
): number {
  return Number(a.originalId) - Number(b.originalId);
}

export function isVrboMarket(value: unknown): value is VrboMarket {
  return VRBO_MARKETS.some((market) => market === value);
}

export function vrboCardActions(
  status: VrboConnectionStatus,
): VrboCardAction[] {
  return VRBO_CARD_ACTIONS[status];
}

export function vrboStatusBadgeClass(status: VrboConnectionStatus): string {
  return VRBO_STATUS_BADGE_CLASSES[status];
}

export function vrboListingStatus(
  link: { status: string | null } | undefined,
): VrboListingStatus {
  if (!link) {
    return 'not_linked';
  }

  return VRBO_LINK_STATUSES[link.status ?? ''] ?? 'linked';
}

export function vrboListingStatusBadgeClass(status: VrboListingStatus): string {
  return VRBO_LISTING_STATUS_BADGE_CLASSES[status];
}

export function vrboBrandUrl(data: IVrboConnectionData): string | null {
  const { sign_up_url: signUpUrl, advertiser_id: advertiserId } = data;

  if (!signUpUrl) {
    return null;
  }

  if (!advertiserId) {
    return signUpUrl;
  }

  const prefill = window.btoa(
    `softwareId=uplisting&advertiserAssignedId=${advertiserId}`,
  );

  return `${signUpUrl}?prefill=${prefill}`;
}

export function parseVrboMappingErrors(
  title: string | null | undefined,
): IVrboMappingErrors {
  const errors: IVrboMappingErrors = {
    general: [],
    byListing: {},
    byProperty: {},
  };

  (title ?? '')
    .split('; ')
    .map((message) => message.trim())
    .filter(Boolean)
    .forEach((message) => {
      const listingId = LISTING_ERROR.exec(message)?.[1];
      const propertyId = PROPERTY_ERROR.exec(message)?.[1];

      if (listingId) {
        errors.byListing[listingId] = [
          ...(errors.byListing[listingId] ?? []),
          message,
        ];
      } else if (propertyId) {
        errors.byProperty[propertyId] = [
          ...(errors.byProperty[propertyId] ?? []),
          message,
        ];
      } else {
        errors.general.push(message);
      }
    });

  return errors;
}

export function vrboMappingStage(
  status: string | null | undefined,
): VrboMappingStage {
  const known = VRBO_MAPPING_PROGRESS_STATUSES.find((item) => item === status);

  return known ? VRBO_MAPPING_STAGES[known] : 'in_progress';
}

export function vrboMappingStageBadgeClass(stage: VrboMappingStage): string {
  return VRBO_MAPPING_STAGE_BADGE_CLASSES[stage];
}

function progressErrors(
  items: { eg_property_id: string; error: IVrboMappingProgressError | null }[],
): IVrboMappingPropertyError[] {
  return items.flatMap(({ eg_property_id: externalId, error }) =>
    error ? [{ externalId, code: error.code, message: error.message }] : [],
  );
}

function countMapped(
  items: { status: VrboMappingProcessingStatus | null }[],
): number {
  return items.filter(({ status }) => status === 'MAPPED').length;
}

export function summariseVrboMappingProgress(
  progress: IVrboMappingProgress | null | undefined,
): IVrboMappingProgressSummary | null {
  if (!progress) {
    return null;
  }

  const properties = progress.properties ?? [];
  const reservations = progress.reservations ?? [];
  const stage = vrboMappingStage(progress.status);
  const reservationStage = vrboMappingStage(progress.reservation_status);
  const checkedAt = progress.checked_at ? new Date(progress.checked_at) : null;

  return {
    stage,
    stageBadgeClass: vrboMappingStageBadgeClass(stage),
    reservationStage,
    reservationStageBadgeClass: vrboMappingStageBadgeClass(reservationStage),
    propertiesTotal: properties.length,
    propertiesComplete: countMapped(properties),
    reservationsTotal: reservations.length,
    reservationsComplete: countMapped(reservations),
    missingPropertyIds: progress.missing_property_ids ?? [],
    missingReservationsCount: (progress.missing_reservations ?? []).length,
    propertyErrors: progressErrors(properties),
    reservationErrors: progressErrors(reservations),
    checkedAt:
      checkedAt && !Number.isNaN(checkedAt.getTime()) ? checkedAt : null,
  };
}

export function vrboMappingChoicesFromLinks(
  links: { externalId: string; propertyId: string | null; isNew?: boolean }[],
): VrboMappingChoices {
  return links
    .filter((link) => !link.isNew)
    .reduce<VrboMappingChoices>((choices, { externalId, propertyId }) => {
      choices[externalId] = propertyId
        ? { kind: 'map', propertyId: `${propertyId}` }
        : { kind: 'import' };

      return choices;
    }, {});
}

export function vrboMappingOutcome(
  choice: VrboMappingChoice | undefined,
): VrboMappingOutcome {
  return choice?.kind ?? 'archive';
}

export function buildVrboMappingPayload(
  listings: { externalId: string }[],
  choices: VrboMappingChoices,
): IVrboMappingPayload {
  const externalIds = [
    ...new Set(listings.map(({ externalId }) => externalId)),
  ];

  return externalIds.reduce<IVrboMappingPayload>(
    (payload, externalId) => {
      const choice = choices[externalId];

      if (!choice) {
        payload.archiveExternalIds.push(externalId);
      } else {
        payload.links.push({
          externalId,
          propertyId: choice.kind === 'map' ? choice.propertyId : null,
        });
      }

      return payload;
    },
    { links: [], archiveExternalIds: [] },
  );
}
